import { useTheme } from '@mui/material'
import { PieChartComponent } from '../../../common/components/PieChartComponent/PieChartComponent'
import { useStyles } from '../RevenueByProductChart/RevenueByProductChart.styles'
import React from 'react'
import { PieCustomTooltip } from '../RevenueByProductChart/PieCustomTooltip/PieCustomTooltip'
import { CustomLegends } from '../CustomLegends/CustomLegends'
import { numberFormat } from '@labrav/common'
import { useSelector } from '../../../store/hooks'
import { Overview } from '../../../modules/flyoutOverview/types'
import { OverviewEmptyState } from '../OverviewEmptyState/OverviewEmptyState'
import OverviewEmptyStateImage from '../../../common/images/overviewEmptyState.png'
import { getColorForProduct } from '../RevenueByProductChart/RevenueByProductChart'

export const RevenueByTypeChart = () => {
  const { classes } = useStyles()
  const theme = useTheme()

  const { revenue } = useSelector<Overview>(
    state => state.flyoutOverview?.overview
  )
  const isRevenueViaISVDataEmpty =
    !revenue?.typeRevenue || revenue.typeRevenue.length === 0

  const revenueByType = revenue?.typeRevenue ?? []
  const datapie = revenueByType.slice(0, 3)
  const otherTypes = revenueByType[3]?.otherTypes ?? []
  const otherTypesTotalValue = revenueByType[3]?.value || 0
  const otherTypesTotalPercentage = revenueByType[3]?.percentage || 0

  const topProducts = [
    ...datapie
      .filter(item => item.value !== 0)
      .map((item, index) => ({
        id: item.type,
        label: item.type,
        value: item.value,
        color: getColorForProduct(index),
      })),
    ...(otherTypesTotalValue !== 0
      ? [
          {
            id: 'Other',
            label: 'Other Types',
            value: otherTypesTotalValue,
            color: getColorForProduct(3),
          },
        ]
      : []),
  ]

  const percentageData: Record<string, number> = {
    ...datapie.reduce(
      (acc, item) => ({
        ...acc,
        [item.type]: item.percentage,
      }),
      {} as Record<string, number>
    ),
    Other: otherTypesTotalPercentage,
  }

  const transformedOtherProducts = otherTypes
    .filter(item => item.value !== 0)
    .map(item => ({
      id: item.type,
      label: item.type,
      value: item.value,
      percentage: otherTypesTotalPercentage,
      color: theme.palette.awsGraph,
    }))

  const customLegendsData = [
    ...datapie
      .filter(item => item.value !== 0)
      .map((item, index) => ({
        label: item.type,
        legendColor: getColorForProduct(index),
        legendSize: 'medium' as const,
        value: `USD ${numberFormat(item.value)}`,
        isKeyValue: true,
      })),
    ...(otherTypesTotalValue !== 0
      ? [
          {
            label: 'Other Products',
            legendColor: theme.palette.microsoftGraph.main,
            legendSize: 'medium' as const,
            value: `USD ${numberFormat(otherTypesTotalValue)}`,
            isKeyValue: true,
          },
        ]
      : []),
  ]

  return (
    <>
      {isRevenueViaISVDataEmpty ? (
        <OverviewEmptyState
          imgUrl={OverviewEmptyStateImage}
          containerHeight={17.5}
          imageHeight={12}
          imageWidth={16.75}
          description="No revenue has been generated by type(s)."
        />
      ) : (
        <div
          className={classes.responsivePieWrapper}
          data-testid={'revenue-by-type-chart'}
        >
          <div className={classes.leftContainer}>
            <PieChartComponent
              data={topProducts}
              height="184px"
              width="176px"
              colors={[
                theme.palette.awsGraph.main,
                theme.palette.gcpGraph.main,
                theme.palette.otherGraph.main,
                theme.palette.microsoftGraph.main,
              ]}
              tooltipContent={point => (
                <PieCustomTooltip
                  preFix="USD"
                  percentage={percentageData[point?.datum?.data.id]}
                  otherProducts={transformedOtherProducts}
                  point={point}
                />
              )}
            />
          </div>
          <div className={classes.rightContainer}>
            <CustomLegends flexDirection="column" legends={customLegendsData} />
          </div>
        </div>
      )}
    </>
  )
}
